/* eslint-disable max-len */
import React, { Fragment, useEffect, useState, createRef } from "react";
import { connect, useSelector } from "react-redux";

import { Link, useHistory } from "react-router-dom";

import { bool, func, shape, string } from "prop-types";
import { routerActions } from "connected-react-router";
import * as Yup from "yup";
import * as i18n from "util/i18n";
import * as configUtils from "util/config";
import { actions as depositsActions, selectors as depositsSelectors } from "reducers/deposits";
import { actions as accountsActions, selectors as accountsSelectors } from "reducers/accounts";
import { actions as menuActions } from "reducers/drawer";
import { actions as settingsActions } from "reducers/settings";

import Notification from "pages/_components/Notification";
import { actions as notificationActions } from "reducers/notification";
import MainContainer from "pages/_components/MainContainer";
import CustomButton from "pages/_components/CustomButton/CustomButton";
import GridTable from "pages/_components/GridTable/GridTable";
import { Field, Form, Formik } from "formik";
import AmountField from "pages/_components/fields/formik/AmountField";
import Selector from "pages/_components/fields/formik/Selector";
import ProductSelector from "pages/_components/fields/formik/FormikProductSelector";
import CustomData from "pages/_components/CustomData/CustomData";
import TextField from "pages/_components/fields/TextField";
import I18n from "pages/_components/I18n";

import Options from "pages/accounts/_components/Options";
import AccountAction from "pages/accounts/_components/AccountAction";

import CustomProductHead from "pages/_components/CustomProductHead";
import ViewTitle from "pages/_components/header/components/ViewTitle";
import Button from "pages/_components/Button";
import gaEventTracker, { events } from "util/gaEventTracker";
import { isIOS } from "react-device-detect";
import { Checkbox } from "pages/_components/fields/Checkbox";

const CATEGORY = "fixedTermDeposit";

const NewDepositRequestForm = ({
    dispatch,
    fetching,
    accounts,
    showLastValues,
    newDepositFormValues,
    newDepositData,
    idTransaction,
    fetchingSimulation,
    ...props
}) => {
    const { isDesktop } = props;
    const [step, setStep] = useState("Simulation");
    const { depositData } = useSelector((state) => state.deposits);
    const { fetching: accountsFetching } = useSelector((state) => state.accounts);
    const isLoading = fetching || accountsFetching;
    const [availableProductTypes, setAvailableProductTypes] = useState([]);
    const [availableAccounts, setAvailableAccounts] = useState({ ARS: [{ id: "1" }], USD: [] });
    const [availableCurrencies, setAvailableCurrencies] = useState(["ARS", "USD"]);
    const [availableExpirationAction, setAvailableExpirationAction] = useState([]);
    const [isSimulating, setIsSimulating] = useState(false);
    const [minInvestmentValue, setMinInvestmentValue] = useState(1000);
    const [minTermDays, setMinTermDays] = useState(30);
    const [maxTermDays, setMaxTermDays] = useState(360);
    const [showOptions, setShowOptions] = useState(false);
    const simulationFormRef = createRef(null);
    const { documents, termsAndConditions } = useSelector((state) => state.settings);
    const [acceptTerm, setAcceptTerm] = useState(false);

    const history = useHistory();
    const depositSimulationData = {
        earnedInterests: newDepositData?.interest,
        chargeAmount: newDepositData?.amountTransfered,
        termInDays: newDepositData?.deadline,
        tna: newDepositData?.rate,
        expirationDate: newDepositData?.dueDate,
        deadline: newDepositData?.coreDeadline,

        retentions: newDepositData?.retentions,
        vats: newDepositData?.vats,
        profit: newDepositData?.profit,
        stamp: newDepositData?.stamp,
        jurisdiction: newDepositData?.jurisdiction,
    };

    useEffect(() => {
        const minAmount = configUtils.get(`deposits.create.min.value.${availableCurrencies[0]}`);
        const minDays = configUtils.get("deposits.min.termInDays");
        const maxDays = configUtils.get(`deposits.max.termInDays.${availableCurrencies[0]}`);

        setMinInvestmentValue(minAmount);
        setMinTermDays(minDays);
        setMaxTermDays(maxDays);
    }, [availableCurrencies]);

    useEffect(() => {
        dispatch(depositsActions.newDepositFormShouldShowLastValues(false));
        dispatch(settingsActions.getTermsAndConditions(CATEGORY));
    }, []);

    useEffect(() => {
        if (!fetchingSimulation) {
            setIsSimulating(false);

            if (!newDepositData.deadline) {
                setStep("Simulation");
            }
        }
    }, [fetchingSimulation]);

    useEffect(() => {
        dispatch(accountsActions.listAccounts());
        setAvailableProductTypes(
            configUtils
                .getArray("deposit.depositTypes")
                .reduce(
                    (allTypes, newType) => [
                        ...allTypes,
                        { value: newType, label: i18n.get(`deposit.depositTypes.${newType}`) },
                    ],
                    [],
                ),
        );
        setAvailableExpirationAction(
            configUtils
                .getArray("deposit.expirationActions")
                .reduce(
                    (allAct, newAct) => [
                        ...allAct,
                        { value: newAct, label: i18n.get(`deposits.expirationAction.${newAct}`) },
                    ],
                    [],
                ),
        );
    }, [dispatch]);

    useEffect(() => {
        const formatOption = (account) => ({
            balance: { currency: account?.currency, quantity: account?.availableBalance },
            currency: account?.currency,
            frequentDestination: null,
            id: account?.productId,
            label: `${i18n.get(`productType.${account?.accountType}`)} ····${account?.accountNumber?.slice(
                -4,
            )} ${i18n.get(`currency.label.${account?.currency}`)}`,
            minimumPayment: 0,
            permissions: Object.keys(account?.permissions),
            statementsLoan: null,
            totalPayment: 0,
            type: account?.accountType,
        });

        if (accounts?.length) {
            setAvailableAccounts({
                ARS: accounts
                    .filter((acc) => acc.currency === "ARS" && acc.blocked !== "S")
                    ?.map((acc) => formatOption(acc)),
                USD: accounts
                    .filter((acc) => acc.currency === "USD" && acc.blocked !== "S")
                    ?.map((acc) => formatOption(acc)),
            });
        }
    }, [accounts]);

    const requestConstitution = () => {
        if (acceptTerm) {
            gaEventTracker.triggerEvent(events.pf_nuevo_constituir());
            dispatch(depositsActions.setDepositTicketSignable(false));
            history.push({
                pathname: "/deposits/summaryForm",
                state: { isCreationFlow: true },
            });
        }
    };

    const REQUIRED_TEXT = i18n.get("deposits.request.required");
    const FORM_ID = "deposits.request";
    const formikProps = {
        initialValues: {
            depositType: showLastValues ? newDepositFormValues?.depositType || "" : "",
            investCapital: showLastValues ? { amount: newDepositFormValues?.investCapital } : {}, // {amount: 150}
            debitAccount: showLastValues ? newDepositFormValues?.debitAccount?.number || "" : "",
            termInDays: showLastValues ? newDepositFormValues?.termInDays || 30 : 30,
            expirationAction: showLastValues ? newDepositFormValues?.expirationAction || "" : "",
        },
        validationSchema: Yup.object({
            depositType: Yup.string().when(["debitAccount"], (debitAccount, schema) => {
                if (!debitAccount) {
                    return schema.required(REQUIRED_TEXT);
                }
                const accountCurrency = accounts.find((acc) => acc.productId === debitAccount).currency;
                if (accountCurrency === "ARS") {
                    return schema.oneOf(["PF-ARS", "TRANS-ARS"], "No coincide con la cuenta").required(REQUIRED_TEXT);
                }
                if (accountCurrency === "USD") {
                    return schema.oneOf(["PF-USD", "TRANS-USD"], "No coincide con la cuenta").required(REQUIRED_TEXT);
                }
                return null;
            }),
            investCapital: Yup.object({
                amount: Yup.number()
                    .typeError(REQUIRED_TEXT)
                    .min(minInvestmentValue, i18n.get("deposits.termInDays.error"))
                    .required(REQUIRED_TEXT),
            }),
            debitAccount: Yup.string().required(REQUIRED_TEXT),
            termInDays: Yup.number()
                .required(REQUIRED_TEXT)
                .integer(i18n.get("deposits.termInDays.error"))
                .test(
                    "validDays",
                    i18n.get("deposits.termInDays.error"), // eslint-disable-next-line func-names
                    function(value) {
                        const MIN_TERM_IN_DAYS = configUtils.getInteger("deposits.min.termInDays", 30);
                        // eslint-disable-next-line react/no-this-in-sfc
                        const { investCapital = {} } = this.parent;
                        const { currency = "" } = investCapital;
                        if (currency === "USD") {
                            const MAX_TERM_IN_DAYS = configUtils.getInteger("deposits.max.termInDays.USD", 365);
                            return MIN_TERM_IN_DAYS <= value && value <= MAX_TERM_IN_DAYS;
                        }
                        if (currency === "ARS") {
                            const MAX_TERM_IN_DAYS = configUtils.getInteger("deposits.max.termInDays.ARS", 360);
                            return MIN_TERM_IN_DAYS <= value && value <= MAX_TERM_IN_DAYS;
                        }
                        return true;
                    },
                ),
            expirationAction: Yup.string().required(REQUIRED_TEXT),
        }),
        onSubmit: (values) => {
            setIsSimulating(true);
            const debitAccountSelected = accounts.find((acc) => acc.productId === values.debitAccount);
            const finalValues = {
                ...values,
                investCapital: values.investCapital.amount,
                debitAccount: {
                    number: values.debitAccount,
                    productType: `${debitAccountSelected.accountType}-${debitAccountSelected.currency}`,
                },
            };

            if (debitAccountSelected?.availableBalance < values?.investCapital?.amount) {
                setIsSimulating(false);
                dispatch(
                    notificationActions.showNotification(i18n.get("echeq.incorrect.amount"), "error", ["deposits"]),
                );
            } else {
                gaEventTracker.triggerEvent(
                    events.pf_nuevo_simular({
                        tipo: values?.depositType,
                        capital: values?.investCapital,
                        plazo: values?.termInDays,
                        accion: values?.expirationAction,
                    }),
                );
                dispatch(depositsActions.fetchNewDepositSimulation(finalValues));
                setStep("Constitution");
            }
        },
        validateOnChange: false,
        validateOnBlur: true,
    };

    const handleClick = () => {
        setShowOptions(true);
        dispatch(menuActions.menuShow());
    };

    const resetForm = (allFormikProps) => {
        allFormikProps.setFieldValue("depositType", "");
        allFormikProps.setFieldValue("investCapital", "");
        allFormikProps.setFieldValue("debitAccount", "");
        allFormikProps.setFieldValue("termInDays", 30);
        allFormikProps.setFieldValue("expirationAction", "");
        allFormikProps.setTouched("depositType", false);
        allFormikProps.setTouched("investCapital", false);
        allFormikProps.setTouched("debitAccount", false);
        allFormikProps.setTouched("termInDays", false);
        allFormikProps.setTouched("expirationAction", false);
    };

    const renderHeader = () => {
        const renderMenuOption = () => (
            <>
                {isDesktop ? (
                    <>
                        <div className="toolbar-item add-button" key="add">
                            <Link className="btn btn-outline btn-block" to="/deposits/rates">
                                <I18n id="deposits.request.rateInquiryBtn" />
                            </Link>
                        </div>
                    </>
                ) : (
                    <div className="toolbar-item commands-mobile">
                        <Button
                            className="toolbar-btn view-options"
                            onClick={handleClick}
                            image={isIOS ? "images/figma/icon/menu-ios-white.svg" : "images/figma/icon/menu-ios.svg"}
                            label="view more options"
                            block={false}
                            bsStyle="link"
                        />
                    </div>
                )}
            </>
        );
        return (
            <CustomProductHead
                titleId="deposits.request.title"
                className="new-deposit-request"
                backLinkTo="/deposits"
                addLinkToLabel="deposits.request.rateInquiryBtn"
                addLinkTo="/deposits/rates"
                renderMenuOption={renderMenuOption}>
                <div className="toolbar-item view-title">
                    <ViewTitle id="deposits.request.title" />
                </div>
            </CustomProductHead>
        );
    };

    return (
        <Fragment>
            <Notification scopeToShow="deposits" />
            {!isLoading && renderHeader()}
            <MainContainer showLoader={isLoading}>
                <div className="container">
                    <Formik {...formikProps} ref={simulationFormRef}>
                        {(allFormikProps) => (
                            <Form id={FORM_ID}>
                                <div className="grid-table form deposits-request grid grid-cols-12">
                                    <div className="grid-table-row md:grid-cols-2 md:col-start-2 md:col-span-10">
                                        <Field idForm={FORM_ID} name="depositType" className="grid-table-data">
                                            {({ field, form }) => (
                                                <Selector
                                                    field={field}
                                                    form={form}
                                                    idForm={FORM_ID}
                                                    label={`${i18n.get("deposits.type")}`}
                                                    placeholder={i18n.get(
                                                        "global.select.option.placeholder",
                                                    )}
                                                    disabled={step === "Constitution"}
                                                    onChange={(newValue) => {
                                                        const currency = newValue.split("-")[1];

                                                        allFormikProps.setFieldValue("investCapital", {
                                                            amount: "",
                                                            currency,
                                                        });
                                                        allFormikProps.setFieldValue("debitAccount", "");

                                                        dispatch(
                                                            depositsActions.setDepositData({
                                                                ...depositData,
                                                                depositType: newValue,
                                                            }),
                                                        );
                                                        setAvailableCurrencies([currency]);
                                                    }}
                                                    options={availableProductTypes}
                                                />
                                            )}
                                        </Field>

                                        <Field
                                            idForm={FORM_ID}
                                            name="investCapital"
                                            component={AmountField}
                                            className="grid-table-data"
                                            disable={step === "Constitution"}
                                            data={{
                                                options: [
                                                    {
                                                        id: 1,
                                                        label: i18n.get(`currency.label.${availableCurrencies[0]}`),
                                                    },
                                                ],
                                            }}
                                            clearable={false}
                                            labelText={`${i18n.get("deposits.investCapital")}`}
                                            onBlurChange={() => {
                                                dispatch(
                                                    depositsActions.setDepositData({
                                                        ...depositData,
                                                        investCapital: allFormikProps.values.investCapital,
                                                    }),
                                                );
                                            }}
                                            hintLabel={
                                                <span className="right">
                                                    {i18n.get("deposits.create.investCapital.min")} {i18n.get(
                                                        `currency.label.${availableCurrencies[0]}`,
                                                    )} {minInvestmentValue?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".")}
                                                </span>
                                            }
                                        />
                                    </div>
                                    <div className="grid-table-row md:col-start-2 md:col-span-10">
                                        <Field
                                            idForm={FORM_ID}
                                            name="debitAccount"
                                            component={ProductSelector}
                                            disabled={step === "Constitution"}
                                            label={`${i18n.get("deposits.debitAccount")}`}
                                            placeholder={i18n.get("global.select.account.placeholder")}
                                            data={{
                                                options: availableCurrencies.reduce(
                                                    (prevOpts, actCurr) => [...prevOpts, ...availableAccounts[actCurr]],
                                                    [],
                                                ),
                                            }}
                                            onChange={(value) => {
                                                dispatch(
                                                    depositsActions.setDepositData({
                                                        ...depositData,
                                                        debitAccount: value,
                                                    }),
                                                );
                                            }}
                                        />
                                    </div>
                                    <div className="grid-table-row md:grid-cols-2 md:col-start-2 md:col-span-10">
                                        <Field
                                            component={TextField}
                                            disabled={step === "Constitution"}
                                            hidePlaceholder
                                            idField="termInDays"
                                            idForm={FORM_ID}
                                            name="termInDays"
                                            min={0}
                                            type="number"
                                            handleOnChange={(e) => {
                                                dispatch(
                                                    depositsActions.setDepositData({
                                                        ...depositData,
                                                        termInDays: Number(e.target.value),
                                                    }),
                                                );
                                            }}
                                            hintLabel={
                                                <span className="left-black">
                                                    {i18n.get("deposits.create.termDays", null, {
                                                        MIN_DAYS: minTermDays,
                                                        MAX_DAYS: maxTermDays,
                                                    })}
                                                </span>
                                            }
                                        />
                                        <Field
                                            className="grid-table-data"
                                            idForm={FORM_ID}
                                            name="expirationAction"
                                            component={Selector}
                                            disabled={step === "Constitution"}
                                            label={`${i18n.get("deposits.expirationAction.label")}`}
                                            placeholder={i18n.get(
                                                "global.select.option.placeholder",
                                            )}
                                            options={availableExpirationAction}
                                            onChange={(value) => {
                                                dispatch(
                                                    depositsActions.setDepositData({
                                                        ...depositData,
                                                        expirationAction: value,
                                                    }),
                                                );
                                            }}
                                        />
                                    </div>
                                </div>
                                {step === "Constitution" && !isSimulating && (
                                    <GridTable className="form-summary">
                                        <GridTable.Container>
                                            <GridTable.Data columnStart={1} alignX="left">
                                                <CustomData
                                                    titleText="deposits.earnedInterests"
                                                    titleTextType="I18N"
                                                    valueText={depositSimulationData.earnedInterests || "$ 0,00"}
                                                    valueTextType={newDepositData?.currency}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={2}>
                                                <CustomData
                                                    titleText="deposits.chargeAmount"
                                                    titleTextType="I18N"
                                                    valueText={depositSimulationData.chargeAmount || "$ 0,00"}
                                                    valueTextType={newDepositData?.currency}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={3}>
                                                <CustomData
                                                    titleText="deposits.termInDays"
                                                    titleTextType="I18N"
                                                    valueText={depositSimulationData.deadline || ""}
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={1} rowStart={2}>
                                                <CustomData
                                                    titleText="deposits.tnaDetail"
                                                    titleTextType="I18N"
                                                    valueText={depositSimulationData.tna || ""}
                                                    valueTextType="PERCENTAGE"
                                                />
                                            </GridTable.Data>
                                            <GridTable.Data columnStart={2} rowStart={2}>
                                                <CustomData
                                                    titleText="deposits.expirationDate"
                                                    titleTextType="I18N"
                                                    valueText={depositSimulationData.expirationDate || ""}
                                                    valueTextType="DATE"
                                                />
                                            </GridTable.Data>

                                            {depositSimulationData?.profit && (
                                                <>
                                                    <GridTable.Data
                                                        columnStart={1}
                                                        columnWidth={3}
                                                        rowStart={3}
                                                        className="">
                                                        <CustomData
                                                            titleText="deposits.retentions.perceptions"
                                                            titleTextType="I18N"
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data
                                                        columnStart={1}
                                                        columnWidth={3}
                                                        rowStart={4}
                                                        className="">
                                                        <CustomData
                                                            titleText="deposits.profits"
                                                            titleTextType="I18N"
                                                            valueText={depositSimulationData.profit || "$ 0,00"}
                                                            valueTextType={newDepositData?.currency}
                                                        />
                                                    </GridTable.Data>
                                                </>
                                            )}

                                            {depositSimulationData?.stamp && (
                                                <>
                                                    <GridTable.Data
                                                        columnStart={1}
                                                        columnWidth={3}
                                                        rowStart={5}
                                                        className="">
                                                        <CustomData
                                                            titleText="deposits.stampCode"
                                                            titleTextType="I18N"
                                                            valueText={depositSimulationData?.stamp || "$ 0,00"}
                                                            valueTextType={newDepositData?.currency}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data
                                                        columnStart={2}
                                                        columnWidth={3}
                                                        rowStart={5}
                                                        className="">
                                                        <CustomData
                                                            titleText="deposits.jurisdiction"
                                                            titleTextType="I18N"
                                                            valueText={depositSimulationData?.jurisdiction}
                                                        />
                                                    </GridTable.Data>
                                                </>
                                            )}
                                        </GridTable.Container>
                                    </GridTable>
                                )}

                                {step === "Constitution" && !isSimulating && (
                                    <>
                                        <div className="box info mt-6 mb-8">
                                            <GridTable>
                                                <GridTable.Container>
                                                    <GridTable.Data columnStart={1} alignX="left">
                                                        <Checkbox
                                                            name="acceptBtn"
                                                            hideLabel
                                                            onChange={() => setAcceptTerm(!acceptTerm)}
                                                            value={acceptTerm}
                                                            checked={acceptTerm}
                                                            formGroup={false}
                                                        />
                                                    </GridTable.Data>
                                                    <GridTable.Data columnStart={2}>
                                                        <I18n defaultValue={termsAndConditions} />
                                                    </GridTable.Data>
                                                </GridTable.Container>
                                            </GridTable>
                                        </div>

                                        <div className="container--alert" role="alert">
                                            <div className="alert alert-info">
                                                <CustomData
                                                    icon="information_fill.svg"
                                                    titleText={i18n.get("discounts.amountToCredit.echeq.disclaimer")}
                                                    keepSpaceIfNoValue={false}
                                                />
                                            </div>
                                        </div>
                                    </>
                                )}
                                <GridTable className={isIOS ? "grid-table-buttons mb-8" : "grid-table-buttons"}>
                                    <GridTable.Container>
                                        <GridTable.Data columnStart={7} columnWidth={4}>
                                            <CustomButton
                                                {...(step === "Simulation"
                                                    ? {
                                                          text: i18n.get("global.simulate"),
                                                          func: () => simulationFormRef.current.submitForm(),
                                                      }
                                                    : {
                                                          text: i18n.get("global.constitute"),
                                                          func: requestConstitution,
                                                      })}
                                                className="btn-primary flex"
                                                loading={isSimulating}
                                                disabled={step === "Simulation" ? false : !acceptTerm}
                                            />
                                        </GridTable.Data>
                                        <GridTable.Data
                                            className={isIOS ? "mt-5" : null}
                                            columnStart={3}
                                            columnWidth={4}>
                                            <CustomButton
                                                {...(step === "Simulation"
                                                    ? {
                                                          text: i18n.get("global.cancel"),
                                                          func: () => dispatch(routerActions.push("/deposits")),
                                                      }
                                                    : {
                                                          text: i18n.get("global.simulateAgain"),
                                                          func: () => {
                                                              dispatch(depositsActions.cleanNewDepositData());
                                                              setStep("Simulation");
                                                              dispatch(depositsActions.clearDepositData());
                                                              resetForm(allFormikProps);
                                                          },
                                                      })}
                                                className="btn-outline flex"
                                            />
                                        </GridTable.Data>
                                    </GridTable.Container>
                                </GridTable>
                            </Form>
                        )}
                    </Formik>
                </div>
            </MainContainer>
            {!isDesktop && showOptions && (
                <Options>
                    <AccountAction
                        isDesktop={isDesktop}
                        labelKey="deposits.request.rateInquiryBtn"
                        to="/deposits/rates"
                    />
                </Options>
            )}
        </Fragment>
    );
};

NewDepositRequestForm.propTypes = {
    fetching: bool,
    accounts: shape({}).isRequired,
    newDepositFormValues: shape({}).isRequired,
    showLastValues: bool.isRequired,
    newDepositData: shape({}).isRequired,
    dispatch: func.isRequired,
    isDesktop: bool.isRequired,
    fetchingSimulation: bool.isRequired,
    idTransaction: string,
};

NewDepositRequestForm.defaultProps = {
    fetching: false,
};

const mapStateToProps = (state) => ({
    fetching: depositsSelectors.getFetching(state),
    accounts: accountsSelectors.getAccounts(state),
    newDepositFormValues: depositsSelectors.getNewDepositFormValues(state),
    showLastValues: depositsSelectors.getNewDepositFormShouldShowLastValues(state),
    newDepositData: depositsSelectors.getNewDepositData(state),
    fetchingSimulation: depositsSelectors.getFetchingSimulation(state),
    idTransaction: depositsSelectors.getIdSimulationTransaction(state),
});

export default connect(mapStateToProps)(NewDepositRequestForm);
